import { type FC, useMemo, useState } from 'react';
import { ProductLineAudio } from '@/app/components/icons/ProductLineAudio';
import { RefreshIcon } from '@/app/components/icons/RefreshIcon';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import type { SourceAudio, VideoPrompt } from '@/app/types';
import { cn } from '@/app/utils';

const TRY_SAMPLES_GALLERY_TEST_ID = 'try-samples';
const TRY_SAMPLE_ITEM_TEST_ID = 'try-sample-item';

export type Example = {
  images: {
    sourceImage: string;
    sourceImagePreview: string;
    selectedImage: string;
    selectedImagePreview: string;
  }[];
  videos?: VideoPrompt[];
  audios?: SourceAudio[];
  text: string;
  thumbnail?: string;
};

interface TryImageSamplesProps {
  className?: string;
  examples: Example[];
  onClickExample: (exampleIdx: number) => void;
}

const MAX_DISPLAY_COUNT = 5;

/**
 * 按顺序获取下一批示例，不足时从头补全
 * @param examples 所有示例
 * @param startIndex 起始索引
 * @param count 需要获取的数量
 */
export const getSequentialExamples = <T,>(examples: T[], startIndex: number, count: number): T[] => {
  if (examples.length <= count) {
    return examples;
  }

  const result: T[] = [];
  for (let i = 0; i < count; i++) {
    const index = (startIndex + i) % examples.length;
    result.push(examples[index]);
  }
  return result;
};

export const TryImageSamples: FC<TryImageSamplesProps> = ({ className, examples, onClickExample }) => {
  const [startIndex, setStartIndex] = useState(() => Math.floor(Math.random() * examples.length));

  const displayedExamples = useMemo(() => {
    return getSequentialExamples(examples, startIndex, MAX_DISPLAY_COUNT);
  }, [examples, startIndex]);

  const handleRefresh = () => {
    setStartIndex((prev) => (prev + MAX_DISPLAY_COUNT) % examples.length);
  };

  const shouldShowRefreshButton = examples.length > MAX_DISPLAY_COUNT;

  return (
    <div className={cn('flex gap-2', className)} data-testid={TRY_SAMPLES_GALLERY_TEST_ID}>
      {displayedExamples.map((example) => {
        const originalIndex = examples.indexOf(example);
        return (
          <CustomTooltip
            key={`${originalIndex}-${example.text}`}
            content={
              <div className="flex h-[98px] gap-2">
                {(example.audios ?? []).map((audio) => (
                  <div
                    key={audio.src}
                    className="flex min-w-20 items-center justify-center rounded-6 bg-system-white08"
                  >
                    <ProductLineAudio className="size-6 text-white" />
                    <span className="ml-1 text-xs">{audio.duration ? `${audio.duration}s` : ''}</span>
                  </div>
                ))}
                {(example.videos || []).map((video) => (
                  <div key={video.videoPreview} className="relative h-full shrink-0">
                    <ImageWithPlaceholder
                      src={video.coverImage}
                      alt="Sample"
                      className="h-full"
                      placeHolderClassName="min-w-[80px]"
                    />
                    <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                      <div className="flex size-6 items-center justify-center rounded-full bg-white/40">
                        <div className="ml-1 border-y-[6px] border-y-transparent border-l-[10px] border-l-white" />
                      </div>
                    </div>
                  </div>
                ))}
                {example.images.map((image) => (
                  <ImageWithPlaceholder
                    key={image.sourceImagePreview}
                    src={image.sourceImagePreview}
                    alt="Sample"
                    className="h-full shrink-0"
                    placeHolderClassName="min-w-[80px]"
                  />
                ))}
              </div>
            }
            sideOffset={12}
            align="center"
            trigger="hover"
            variant="extend"
            contentClassName="max-w-fit"
          >
            <div
              onClick={() => onClickExample(originalIndex)}
              className="size-[30px] shrink-0 cursor-pointer overflow-hidden rounded-6"
              data-testid={TRY_SAMPLE_ITEM_TEST_ID}
            >
              {example.thumbnail || example.images[0]?.sourceImagePreview || example.videos?.[0]?.coverImage ? (
                <ImageWithPlaceholder
                  src={example.thumbnail ?? example.images[0]?.sourceImagePreview ?? example.videos?.[0]?.coverImage}
                  alt="Sample"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex size-full items-center justify-center bg-system-white08">
                  <ProductLineAudio className="size-5 text-white" />
                </div>
              )}
            </div>
          </CustomTooltip>
        );
      })}
      {shouldShowRefreshButton && (
        <span
          onClick={handleRefresh}
          className="flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-6 border border-system-white16 transition-colors hover:bg-system-white04"
          aria-label="Refresh samples"
        >
          <RefreshIcon className="size-4 text-white" />
        </span>
      )}
    </div>
  );
};
